/**
 * Presentation helpers for the intent layer.
 *
 * Pure and dependency-free so the formatting rules the Plan page relies on are
 * unit-testable without rendering anything.
 */
import type { GoalWithProgress, Nudge, RoutineOccurrence, SlotState } from '../api.js';

/** Cadence blob → the shape the schedule editor edits. */
export interface ScheduleDraft {
  type: 'times_per_day' | 'days_per_week' | 'interval_days';
  times: string[];
  days: number[];
  every: number;
  graceMinutes: number;
}

export const DEFAULT_SCHEDULE: ScheduleDraft = {
  type: 'times_per_day',
  times: ['08:00', '20:00'],
  days: [1, 3, 5],
  every: 2,
  graceMinutes: 180,
};

/** Draft → the wire shape the server validates. Drops fields the type ignores. */
export function toSchedulePayload(d: ScheduleDraft): Record<string, unknown> {
  const base = { type: d.type, times: d.times, graceMinutes: d.graceMinutes };
  if (d.type === 'days_per_week') return { ...base, days: d.days };
  if (d.type === 'interval_days') return { ...base, every: d.every };
  return base;
}

/** Stored schedule blob → a draft the editor can round-trip. */
export function toScheduleDraft(raw: unknown): ScheduleDraft {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SCHEDULE };
  const s = raw as Record<string, unknown>;
  const type =
    s.type === 'days_per_week' || s.type === 'interval_days' ? s.type : 'times_per_day';
  return {
    type,
    times: Array.isArray(s.times) && s.times.every((t) => typeof t === 'string')
      ? (s.times as string[])
      : DEFAULT_SCHEDULE.times,
    days: Array.isArray(s.days) && s.days.every((d) => typeof d === 'number')
      ? (s.days as number[])
      : DEFAULT_SCHEDULE.days,
    every: typeof s.every === 'number' ? s.every : DEFAULT_SCHEDULE.every,
    graceMinutes: typeof s.graceMinutes === 'number' ? s.graceMinutes : DEFAULT_SCHEDULE.graceMinutes,
  };
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Human cadence line, mirroring the server's `describeSchedule`. */
export function describeSchedule(raw: unknown): string {
  const d = toScheduleDraft(raw);
  const at = `at ${d.times.join(', ')}`;
  if (d.type === 'days_per_week') return `${d.days.map((n) => DAY_NAMES[n]).join(', ')} ${at}`;
  if (d.type === 'interval_days') {
    return d.every === 1 ? `daily ${at}` : `every ${d.every} days ${at}`;
  }
  return `${d.times.length}×/day ${at}`;
}

/** Palette slot for a goal's standing, matching the app's semantic hues. */
export function goalTone(entry: GoalWithProgress): 'good' | 'warn' | 'bad' | 'muted' {
  if (entry.goal.status === 'achieved') return 'good';
  if (entry.metric) {
    if (entry.metric.status === 'achieved' || entry.metric.status === 'ahead') return 'good';
    if (entry.metric.status === 'behind') return 'bad';
    if (entry.metric.status === 'on_track') return 'good';
    return 'muted';
  }
  if (entry.habit) {
    if (entry.habit.status === 'strong') return 'good';
    if (entry.habit.status === 'slipping') return 'warn';
    if (entry.habit.status === 'broken') return 'bad';
  }
  return 'muted';
}

/** 0–100 completion for the progress bar; habit goals fall back to adherence. */
export function goalPercent(entry: GoalWithProgress): number | null {
  if (entry.metric) return Math.max(0, Math.min(100, Math.round(entry.metric.fraction * 100)));
  if (entry.habit && entry.habit.settled > 0) return Math.round(entry.habit.rate * 100);
  return null;
}

/** Where the plan says the goal should be right now, as a 0–100 marker. */
export function goalPaceMarker(entry: GoalWithProgress): number | null {
  if (!entry.metric || entry.metric.timeFraction == null) return null;
  return Math.max(0, Math.min(100, Math.round(entry.metric.timeFraction * 100)));
}

/** "in 3 days" / "tomorrow" / "today" / "2 days ago". */
export function relativeDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

/** HH:MM (24h) → a 12-hour label for display. */
export function prettyTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h < 12 ? 'am' : 'pm';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

/** Sort order for today's slots: what needs doing first, done last. */
const STATE_RANK: Record<SlotState, number> = {
  due: 0,
  upcoming: 1,
  missed: 2,
  skipped: 3,
  done: 4,
};

export function sortOccurrences(list: RoutineOccurrence[]): RoutineOccurrence[] {
  return [...list].sort(
    (a, b) => STATE_RANK[a.state] - STATE_RANK[b.state] || a.dueAt.localeCompare(b.dueAt),
  );
}

/** Group nudges by priority band so the inbox leads with what is urgent. */
export function nudgeTone(n: Nudge): 'urgent' | 'important' | 'normal' | 'ambient' {
  if (n.priority >= 3) return 'urgent';
  if (n.priority === 2) return 'important';
  if (n.priority === 1) return 'normal';
  return 'ambient';
}

/** An icon glyph per event kind — small, typographic, no icon font needed. */
export function eventGlyph(kind: string): string {
  switch (kind) {
    case 'vacation':
    case 'travel':
      return '✈';
    case 'birthday':
      return '🎂';
    case 'anniversary':
      return '♥';
    case 'appointment':
      return '⏱';
    case 'deadline':
      return '⚑';
    case 'bill':
    case 'renewal':
      return '$';
    default:
      return '•';
  }
}

/** Format a numeric goal value with its unit, trimming pointless decimals. */
export function fmtGoalValue(v: number | null, unit: string | null): string {
  if (v == null) return '—';
  const rounded = Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
  return unit ? `${rounded} ${unit}` : String(rounded);
}
